import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { z } from 'zod';

import { useApi } from '../../../lib/api/api-context.js';
import { parseOrThrow } from '../../../lib/api/contract.js';
import { queryKeys } from '../../../lib/api/query-keys.js';
import { dailyRateEndpoints } from './endpoints.js';
import { dailyRate, tax, type DailyRate, type Tax } from './schemas.js';

const ratesResponse = z.array(dailyRate);
const taxesResponse = z.array(tax);

/**
 * The published rates for one property/rate plan/room type over a date range.
 */
export function useDailyRates(
  propertyId: string,
  ratePlanId: string,
  roomTypeId: string,
  from: string,
  to: string,
): UseQueryResult<readonly DailyRate[]> {
  const api = useApi();
  const enabled =
    propertyId !== '' && ratePlanId !== '' && roomTypeId !== '' && from !== '' && to !== '';

  return useQuery({
    queryKey: [...queryKeys.dailyRates.all, { propertyId, ratePlanId, roomTypeId, from, to }],
    queryFn: async () => {
      const path = dailyRateEndpoints.rates({ propertyId, ratePlanId, roomTypeId, from, to });
      const result = await api.get<unknown>(path);
      return parseOrThrow(ratesResponse, result, path);
    },
    enabled,
  });
}

/**
 * Taxes configured for the property and currency.
 */
export function useTaxes(propertyId: string, currency: string): UseQueryResult<readonly Tax[]> {
  const api = useApi();
  const enabled = propertyId !== '' && currency !== '';

  return useQuery({
    queryKey: ['taxes', { propertyId, currency }],
    queryFn: async () => {
      const path = dailyRateEndpoints.taxes(propertyId, currency);
      const result = await api.get<unknown>(path);
      return parseOrThrow(taxesResponse, result, path);
    },
    enabled,
  });
}
